import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import jobService from '../../services/jobService';

const EditJobDialog = ({ isOpen, onClose, job, onJobUpdated }) => {
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    employment_type: 'full-time',
    experience_level: 'mid',
    salary_range_min: '',
    salary_range_max: '',
    currency: 'USD',
    description: '',
    requirements: [],
    responsibilities: [],
    skills_required: [],
    skills_preferred: [],
    benefits: [],
    remote_work_allowed: false,
    travel_required: 'none',
    education_required: 'bachelor',
    certifications_required: [],
    application_deadline: '',
    start_date: '',
    reporting_manager: '',
    team_size: '',
    interview_process: [],
    status: 'draft',
    priority: 'medium'
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Input states for arrays
  const [newRequirement, setNewRequirement] = useState('');
  const [newResponsibility, setNewResponsibility] = useState('');
  const [newSkillRequired, setNewSkillRequired] = useState('');
  const [newSkillPreferred, setNewSkillPreferred] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [newInterviewStep, setNewInterviewStep] = useState('');

  useEffect(() => {
    if (isOpen && job) {
      setFormData({
        title: job.title || '',
        department: job.department || '',
        location: job.location || '',
        employment_type: job.employment_type || 'full-time',
        experience_level: job.experience_level || 'mid',
        salary_range_min: job.salary_range_min?.toString() || '',
        salary_range_max: job.salary_range_max?.toString() || '',
        currency: job.currency || 'USD',
        description: job.description || '',
        requirements: job.requirements || [],
        responsibilities: job.responsibilities || [],
        skills_required: job.skills_required || [],
        skills_preferred: job.skills_preferred || [],
        benefits: job.benefits || [],
        remote_work_allowed: job.remote_work_allowed || false,
        travel_required: job.travel_required || 'none',
        education_required: job.education_required || 'bachelor',
        certifications_required: job.certifications_required || [],
        application_deadline: job.application_deadline ? 
          new Date(job.application_deadline).toISOString().split('T')[0] : '',
        start_date: job.start_date ? 
          new Date(job.start_date).toISOString().split('T')[0] : '',
        reporting_manager: job.reporting_manager || '',
        team_size: job.team_size?.toString() || '',
        interview_process: job.interview_process || [],
        status: job.status || 'draft',
        priority: job.priority || 'medium'
      });
      setErrors({});
    }
  }, [isOpen, job]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const addToArray = (arrayName, value, setValue) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [arrayName]: [...prev[arrayName], value.trim()]
      }));
      setValue('');
    }
  };

  const removeFromArray = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const validation = jobService.validateJobData(formData);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Prepare payload with proper data types
      const payload = {
        ...formData,
        salary_range_min: formData.salary_range_min ? parseInt(formData.salary_range_min) : null,
        salary_range_max: formData.salary_range_max ? parseInt(formData.salary_range_max) : null,
        team_size: formData.team_size ? parseInt(formData.team_size) : null,
        application_deadline: formData.application_deadline ? 
          new Date(formData.application_deadline).toISOString() : null,
        start_date: formData.start_date ? 
          new Date(formData.start_date).toISOString() : null,
      };

      const result = await jobService.updateJob(job.id, payload);
      
      console.log('Job updated successfully:', result);
      
      if (onJobUpdated) {
        onJobUpdated(result.job);
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating job:', error);
      setErrors({ submit: error.message || 'Failed to update job. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const ArrayInput = ({ label, arrayName, newValue, setNewValue, placeholder }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="space-y-2">
        <div className="flex space-x-2">
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addToArray(arrayName, newValue, setNewValue);
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => addToArray(arrayName, newValue, setNewValue)}
            className="px-3"
          >
            <Icon name="Plus" size={16} />
          </Button>
        </div>
        
        {formData[arrayName].length > 0 && (
          <div className="space-y-1">
            {formData[arrayName].map((item, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                <span className="text-sm">{item}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFromArray(arrayName, index)}
                  className="text-red-600 hover:text-red-700 p-1"
                >
                  <Icon name="X" size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Job Posting</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-8">
            {/* Basic Information */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Senior Software Engineer"
                  />
                  {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department *
                  </label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Engineering"
                  />
                  {errors.department && <p className="text-red-600 text-sm mt-1">{errors.department}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. San Francisco, CA or Remote"
                  />
                  {errors.location && <p className="text-red-600 text-sm mt-1">{errors.location}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employment Type
                  </label>
                  <select
                    name="employment_type"
                    value={formData.employment_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level
                  </label>
                  <select
                    name="experience_level"
                    value={formData.experience_level}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="lead">Lead</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Salary Information */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Compensation</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Salary
                  </label>
                  <input
                    type="number"
                    name="salary_range_min"
                    value={formData.salary_range_min}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="50000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Salary
                  </label>
                  <input
                    type="number"
                    name="salary_range_max"
                    value={formData.salary_range_max}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="80000"
                  />
                  {errors.salary_range_max && <p className="text-red-600 text-sm mt-1">{errors.salary_range_max}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Job Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Provide a detailed description of the role, company, and what makes this position attractive..."
              />
              {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
            </div>

            {/* Requirements and Responsibilities */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ArrayInput
                label="Requirements"
                arrayName="requirements"
                newValue={newRequirement}
                setNewValue={setNewRequirement}
                placeholder="Add a requirement..."
              />

              <ArrayInput
                label="Responsibilities"
                arrayName="responsibilities"
                newValue={newResponsibility}
                setNewValue={setNewResponsibility}
                placeholder="Add a responsibility..."
              />
            </div>

            {/* Skills */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ArrayInput
                label="Required Skills"
                arrayName="skills_required"
                newValue={newSkillRequired}
                setNewValue={setNewSkillRequired}
                placeholder="Add a required skill..."
              />

              <ArrayInput
                label="Preferred Skills"
                arrayName="skills_preferred"
                newValue={newSkillPreferred}
                setNewValue={setNewSkillPreferred}
                placeholder="Add a preferred skill..."
              />
            </div>

            {/* Additional Information */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Additional Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Travel Required
                  </label>
                  <select
                    name="travel_required"
                    value={formData.travel_required}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="none">None</option>
                    <option value="occasional">Occasional</option>
                    <option value="frequent">Frequent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Education Required
                  </label>
                  <select
                    name="education_required"
                    value={formData.education_required}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="high-school">High School</option>
                    <option value="bachelor">Bachelor's Degree</option>
                    <option value="master">Master's Degree</option>
                    <option value="phd">PhD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reporting Manager
                  </label>
                  <input
                    type="text"
                    name="reporting_manager"
                    value={formData.reporting_manager}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. John Doe, Engineering Manager"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Size
                  </label>
                  <input
                    type="number"
                    name="team_size"
                    value={formData.team_size}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application Deadline
                  </label>
                  <input
                    type="date"
                    name="application_deadline"
                    value={formData.application_deadline}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="remote_work_allowed"
                    name="remote_work_allowed"
                    checked={formData.remote_work_allowed}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remote_work_allowed" className="ml-2 text-sm font-medium text-gray-700">
                    Remote work allowed
                  </label>
                </div>
              </div>
            </div>

            {/* Benefits and Certifications */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ArrayInput
                label="Benefits"
                arrayName="benefits"
                newValue={newBenefit}
                setNewValue={setNewBenefit}
                placeholder="Add a benefit..."
              />

              <ArrayInput
                label="Required Certifications"
                arrayName="certifications_required"
                newValue={newCertification}
                setNewValue={setNewCertification}
                placeholder="Add a certification..."
              />
            </div>

            {/* Interview Process */}
            <ArrayInput
              label="Interview Process"
              arrayName="interview_process"
              newValue={newInterviewStep}
              setNewValue={setNewInterviewStep}
              placeholder="Add an interview step..."
            />

            {/* Status */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Job Status</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="closed">Closed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Active jobs are visible to candidates and accept applications.
                </p>
              </div>
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={loading}
              className="min-w-[120px]"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  Update Job
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditJobDialog;